import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase';
import { sendTextMessage } from '../Helpers';

/**
 * Returns all active announcements (visible === true).
 */
export const getAnnouncement = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'announcements'));
    const announcements = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(a => a.visible !== false); // treat missing visible as true

    return announcements;
  } catch (err) {
    console.error('Error fetching announcements:', err);
    throw err;
  }
};

/**
 * Creates a new visible announcement.
 * @param {string} body
 * @param {string} date
 * @param {string} sender
 * @param {boolean} textStaff - whether to text all staff users
 */
export const newAnnouncement = async ({ body, date, sender, textStaff }) => {
  try {
    // Create a new announcement doc
    const docRef = await addDoc(collection(db, 'announcements'), {
      body: body || '',
      date: date || '',
      sender: sender || '',
      visible: true,
      dismissed: [], // array of client doc ids who have dismissed this
    });

    // SMS blast to staff if requested
    if (textStaff) {
      try {
        const clientsSnapshot = await getDocs(collection(db, 'clients'));
        const staffDocs = clientsSnapshot.docs.filter(d =>
          d.id.endsWith('STAFF')
        );
        const sendPromises = staffDocs.map(d => {
          const staffId = d.id;
          const phone = staffId.replace('STAFF', '');
          return sendTextMessage({
            message: `JMJPC BOOKING: ${sender} has made a new announcement: "${body}"`,
            yourPhoneNumber: '4073059084',
            recipientPhoneNumber: phone,
            senderName: 'JMJPC',
          }).catch(err => {
            console.error(
              `Failed to send announcement SMS to staff ${staffId} (phone: ${phone}):`,
              err
            );
          });
        });
        await Promise.all(sendPromises);
      } catch (err) {
        console.error('Error fetching staff users for SMS:', err);
      }
    }

    const snap = await getDoc(docRef);
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    console.error('Error creating new announcement:', err);
    throw err;
  }
};

/**
 * Ends (hides) a specific announcement.
 * @param {string} announcementId
 */
export const endAnnouncement = async (announcementId) => {
  try {
    const docRef = doc(db, 'announcements', announcementId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      await updateDoc(docRef, { visible: false });
    } else {
      // fallback if doc somehow missing
      await setDoc(docRef, {
        body: '',
        date: '',
        sender: '',
        visible: false,
        dismissed: [],
      });
    }
    const updated = await getDoc(docRef);
    return { id: updated.id, ...updated.data() };
  } catch (err) {
    console.error('Error ending announcement:', err);
    throw err;
  }
};

/**
 * Marks an announcement as dismissed for a given user id (client doc id).
 * @param {string} announcementId
 * @param {string} userId - client doc id from 'clients' collection
 */
export const dismissAnnouncementForUser = async (announcementId, userId) => {
  if (!userId) return;
  try {
    const docRef = doc(db, 'announcements', announcementId);
    await updateDoc(docRef, {
      dismissed: arrayUnion(userId),
    });
  } catch (err) {
    console.error('Error dismissing announcement for user:', err);
    throw err;
  }
};
